import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

export const allocationSerializer = Serializers.object({
  creditId: uuidSerializer,
  debitId: uuidSerializer,
  amount: Serializers.decimal,
});
export type Allocation = SerializerValue<typeof allocationSerializer>;

export const cashRewardBookingTypes = ["salesPremium", "income", "discount"] as const;
export type CashRewardBookingType = (typeof cashRewardBookingTypes)[number];
export const CashRewardBookingType = (str: string): CashRewardBookingType => {
  if (!cashRewardBookingTypes.includes(str as CashRewardBookingType))
    throw new Error(`Invalid cash reward booking type: ${str}`);
  return str as CashRewardBookingType;
};

export const creditSerializer = Serializers.object({
  entryType: Serializers.stringOneOf("credit"),
  id: uuidSerializer,
  userId: Serializers.string,
  time: Serializers.datetime,
  amount: Serializers.decimal,

  bookingType: Serializers.stringOneOf(...cashRewardBookingTypes),
  loyaltyPurchaseId: uuidSerializer.nullable(),
  rewardId: Serializers.string,
});
export type Credit = SerializerValue<typeof creditSerializer>;

export const debitSerializer = Serializers.object({
  entryType: Serializers.stringOneOf("debit"),
  id: uuidSerializer,
  userId: Serializers.string,
  time: Serializers.datetime,
  amount: Serializers.decimal,
  santanderWithdrawalId: Serializers.string.nullable(),
  blikCheckId: Serializers.string.nullable(),
});
export type Debit = SerializerValue<typeof debitSerializer>;

export const historySerializer = Serializers.first(creditSerializer, debitSerializer).array();

export type History = SerializerValue<typeof historySerializer>;

export const accountSerializer = Serializers.object({
  userId: Serializers.string,
  balance: Serializers.decimal,
  history: historySerializer,
  allocations: allocationSerializer.array(),
});
export type Account = SerializerValue<typeof accountSerializer>;

export const accountActions = {
  myAccountQuery: HttpAction({
    path: "/myAccount",
    method: "get",
    valueSerializer: accountSerializer,
    requiresAuth: true,
  }),

  accountQuery: HttpAction({
    path: "/account/:userId",
    method: "get",
    valueSerializer: accountSerializer,
    paramsSerializer: Serializers.object({ userId: Serializers.string }),
    requiresAuth: true,
  }),

  accountsQuery: HttpAction({
    path: "/accounts",
    method: "get",
    qsSerializer: Serializers.object({
      limit: Serializers.integer,
      offset: Serializers.integer,
      excludeZeroBalance: Serializers.boolean.optional(),
      sortByBalance: Serializers.stringOneOf("asc", "desc").optional(),
    }),
    valueSerializer: Serializers.paginatedList(accountSerializer),
    requiresAuth: true,
  }),

  creditAccount: HttpAction({
    requiresAuth: true,
    path: "/account/:userId/creditAccount",
    method: "post",
    paramsSerializer: Serializers.object({ userId: Serializers.string }),
    bodySerializer: Serializers.object({
      ...Serializers.object({
        entryId: uuidSerializer,
      }).partial().fields,
      amount: Serializers.decimal,
      bookingType: Serializers.stringOneOf("salesPremium", "income", "discount"),
      rewardId: Serializers.string,
    }),
  }),

  debitAccount: HttpAction({
    requiresAuth: true,
    path: "/account/:userId/debitAccount",
    method: "post",
    paramsSerializer: Serializers.object({ userId: Serializers.string }),
    bodySerializer: Serializers.object({
      ...Serializers.object({
        entryId: uuidSerializer,
      }).partial().fields,
      amount: Serializers.decimal,
    }),
  }),

  permissableDiscountPoolQuery: HttpAction({
    requiresAuth: true,
    path: "/account/:userId/permissableDiscountPool",
    method: "get",
    paramsSerializer: Serializers.object({ userId: uuidSerializer }),
    valueSerializer: Serializers.object({
      total: Serializers.decimal,
      available: Serializers.decimal,
      used: Serializers.decimal,
    }),
  }),
};
